/**
 * testTypeIcons.ts
 * Maps a scheduling task's test/machine type to a representative MUI icon for the
 * Board-view machine/test rows. Pure (no theme, no hooks) — the caller colours the
 * icon via `sx`. Keyword-based so it's resilient to free-text TaskTypeName values.
 */

import type { SvgIconComponent } from '@mui/icons-material';
import ScienceOutlined from '@mui/icons-material/ScienceOutlined';
import WaterDropOutlined from '@mui/icons-material/WaterDropOutlined';
import CompressOutlined from '@mui/icons-material/CompressOutlined';
import GrainOutlined from '@mui/icons-material/GrainOutlined';
import OpacityOutlined from '@mui/icons-material/OpacityOutlined';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import ContentCutOutlined from '@mui/icons-material/ContentCutOutlined';
import ViewInArOutlined from '@mui/icons-material/ViewInArOutlined';
import SpeedOutlined from '@mui/icons-material/SpeedOutlined';
import TerrainOutlined from '@mui/icons-material/TerrainOutlined';

/** Minimal shape this resolver needs from a task. */
export interface TaskTypeIconInput {
  TaskTypeName: string | null;
  TaskTypeCategory: string | null;
  Name: string;
  IsPrepStep: boolean;
}

// Ordered most-specific → general; first match wins.
const RULES: Array<{ test: RegExp; icon: SvgIconComponent }> = [
  { test: /moist|water content|drying/, icon: WaterDropOutlined },
  { test: /consolidat|compress|oedomet/, icon: CompressOutlined },
  { test: /triaxial|direct shear|\bshear\b/, icon: ViewInArOutlined },
  { test: /sieve|gradation|particle|grain|hydromet/, icon: GrainOutlined },
  { test: /atterberg|plastic|liquid|\blimit/, icon: OpacityOutlined },
  { test: /perme|hydraulic|conductiv/, icon: FilterAltOutlined },
  { test: /proctor|compaction|density|pycnomet|specific gravity/, icon: SpeedOutlined },
  { test: /rock|point load|ucs|brazil|tensile/, icon: TerrainOutlined },
];

/**
 * Pick an icon for a task's test/machine type.
 * Prep steps (cutting/grinding/conformity) always get the "cut" icon; otherwise we
 * keyword-match category → type → name; fallback is a generic lab beaker.
 */
export function iconForTaskType(task: TaskTypeIconInput): SvgIconComponent {
  if (task.IsPrepStep) return ContentCutOutlined;
  const hay = `${task.TaskTypeCategory ?? ''} ${task.TaskTypeName ?? ''} ${task.Name ?? ''}`.toLowerCase();
  for (const r of RULES) if (r.test.test(hay)) return r.icon;
  return ScienceOutlined;
}
